/**
 * Canvas-based text measurement for the auto-fit box behavior, all measured with
 * the element's REAL (computed) font and text-transform so the editor matches
 * the renderer:
 *  - `measureTextInkHeightPx` — the glyph INK height of the wrapped block, so the
 *    box can grow to exactly fit the text (symmetric padding).
 *  - `maxWordWidthPx` — widest unbreakable word, to grow the box width when a
 *    word no longer fits.
 *  - `fitFontToBoxPx` — largest font size that fits a (shrunk) box, to adjust the
 *    font down when the box is dragged smaller.
 */

import { DEFAULT_RELATIVE_PADDING } from "../constants";

const MIN_FONT_SIZE_PX = 5;

let sharedCanvas: HTMLCanvasElement | null = null;
const getCtx = (): CanvasRenderingContext2D | null => {
  if (typeof document === "undefined") return null;
  sharedCanvas ??= document.createElement("canvas");
  return sharedCanvas.getContext("2d");
};

type ComputedFont = { style: string; weight: string; family: string };
const readFont = (el: HTMLElement): ComputedFont => {
  const cs = getComputedStyle(el);
  return { style: cs.fontStyle, weight: cs.fontWeight, family: cs.fontFamily };
};
const fontString = (f: ComputedFont, sizePx: number): string =>
  `${f.style} ${f.weight} ${sizePx}px ${f.family}`;

const fontSizeOf = (el: HTMLElement): number =>
  parseFloat(getComputedStyle(el).fontSize) || 16;

// The text is measured AS RENDERED — apply the element's text-transform so e.g.
// uppercased titles aren't measured (and underestimated) in their stored case.
const applyTransform = (el: HTMLElement, text: string): string => {
  switch (getComputedStyle(el).textTransform) {
    case "uppercase":
      return text.toUpperCase();
    case "lowercase":
      return text.toLowerCase();
    default:
      return text;
  }
};

const splitWords = (text: string): string[] =>
  text.split(/\s+/).filter(Boolean);

const wrapLines = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidthPx: number
): string[] => {
  const lines: string[] = [];
  for (const paragraph of text.split("\n")) {
    const words = splitWords(paragraph);
    if (words.length === 0) {
      lines.push("");
      continue;
    }
    let current = "";
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (ctx.measureText(candidate).width > maxWidthPx && current) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }
    if (current) lines.push(current);
  }
  return lines;
};

/**
 * Glyph INK height (px) of the text wrapped to `availWidthPx`, stacked the way
 * the renderer stacks lines: `firstLineInkAscent + (n−1)·lineHeightPx +
 * lastLineInkDescent`. This is what the box should grow to so the text fits with
 * symmetric (flush) padding. `lineHeightPx` is the rendered line advance.
 */
export const measureTextInkHeightPx = (
  el: HTMLElement,
  text: string,
  availWidthPx: number,
  lineHeightPx: number,
  wrapText: boolean
): number | null => {
  const ctx = getCtx();
  if (!ctx || !text) return null;
  const font = readFont(el);
  if (
    typeof document.fonts.check === "function" &&
    !document.fonts.check(`16px ${font.family}`)
  ) {
    return null;
  }
  ctx.font = fontString(font, fontSizeOf(el));
  const content = applyTransform(el, text);
  const lines =
    wrapText && availWidthPx > 0
      ? wrapLines(ctx, content, availWidthPx)
      : content.split("\n");
  if (lines.length === 0) return 0;
  const first = ctx.measureText(lines[0]?.length ? lines[0] : " ");
  const lastText = lines[lines.length - 1];
  const last = ctx.measureText(lastText?.length ? lastText : " ");
  return (
    first.actualBoundingBoxAscent +
    (lines.length - 1) * lineHeightPx +
    last.actualBoundingBoxDescent
  );
};

/** Widest unbreakable word (as rendered) at the element's current font, in px. */
export const maxWordWidthPx = (el: HTMLElement, text: string): number => {
  const ctx = getCtx();
  if (!ctx) return 0;
  ctx.font = fontString(readFont(el), fontSizeOf(el));
  let max = 0;
  for (const word of splitWords(applyTransform(el, text))) {
    max = Math.max(max, ctx.measureText(word).width);
  }
  return max;
};

/**
 * Largest font size (px, ≤ `maxFontPx`) at which `text` fits the box on both
 * axes. Mirrors the renderer's fit: a word may not exceed the available width
 * and the wrapped block may not exceed the available height. Returns `maxFontPx`
 * when it already fits.
 */
export const fitFontToBoxPx = ({
  el,
  text,
  boxWidthPx,
  boxHeightPx,
  lineHeightRatio,
  wrapText,
  hasBackground,
  maxFontPx,
}: {
  el: HTMLElement;
  text: string;
  boxWidthPx: number;
  boxHeightPx: number;
  lineHeightRatio: number;
  wrapText: boolean;
  hasBackground: boolean;
  maxFontPx: number;
}): number => {
  const ctx = getCtx();
  if (!ctx || !text || boxWidthPx <= 0 || boxHeightPx <= 0) return maxFontPx;
  const font = readFont(el);
  const content = applyTransform(el, text);

  const fits = (sizePx: number): boolean => {
    ctx.font = fontString(font, sizePx);
    // Match the renderer: background text reserves margin = ratio * font size.
    const margin = hasBackground ? DEFAULT_RELATIVE_PADDING * sizePx : 0;
    const availW = boxWidthPx - margin * 2;
    const availH = boxHeightPx - margin * 2;
    if (availW <= 0 || availH <= 0) return false;
    const lines = wrapText
      ? wrapLines(ctx, content, availW)
      : content.split("\n");
    let widest = 0;
    for (const line of lines) {
      widest = Math.max(widest, ctx.measureText(line).width);
    }
    const blockHeight =
      sizePx * lines.length + sizePx * lineHeightRatio * (lines.length - 1);
    return widest <= availW && blockHeight <= availH;
  };

  let size = maxFontPx;
  while (size > MIN_FONT_SIZE_PX && !fits(size)) {
    size -= Math.max(1, Math.trunc(size * 0.05));
  }
  return size;
};
